import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import API, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const COUNTRIES = ["México", "Colombia", "Argentina", "Chile", "Perú", "España", "Estados Unidos", "Ecuador", "Guatemala", "Bolivia", "Uruguay", "Venezuela", "Costa Rica", "Panamá", "Otro"];

const inputCls = "mt-2 w-full rounded-xl border border-[#4b3a2f]/15 bg-white px-4 py-3.5 text-sm text-[#4b3a2f] outline-none transition-colors focus:border-[#c9a24b]";

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", email_confirmation: "", city: "", country: "México", password: "", password_confirmation: "" });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Ingresa tu nombre completo";
    if (!/^[+\d][\d\s()-]{6,}$/.test(form.whatsapp.trim())) errs.whatsapp = "Ingresa un número de WhatsApp válido (ej. +52 55 1234 5678)";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Ingresa un correo válido";
    if (form.email.toLowerCase() !== form.email_confirmation.toLowerCase()) errs.email_confirmation = "Los correos no coinciden";
    if (!form.city.trim()) errs.city = "Ingresa tu ciudad";
    if (form.password.length < 8) errs.password = "Mínimo 8 caracteres";
    if (form.password !== form.password_confirmation) errs.password_confirmation = "Las contraseñas no coinciden";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await register(form);
      toast.success("¡Cuenta creada con éxito! Bienvenido a AWEN");
      navigate(params.get("next") || "/");
    } catch (err) {
      setServerError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const Err = ({ k }) => errors[k] ? <p data-testid={`register-error-${k}`} className="mt-1.5 text-xs text-[#c85a48]">{errors[k]}</p> : null;

  return (
    <div className="min-h-screen bg-[#faf7f2] pb-24 pt-[120px]" data-testid="register-page">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="text-center">
          <img src="/logo-emblem.png" alt="AWEN" className="mx-auto h-20 w-20 rounded-full ring-1 ring-[#c9a24b]/40" />
          <h1 className="mt-6 font-serif-d text-5xl font-semibold text-[#4b3a2f]">Crea tu cuenta AWEN</h1>
          <p className="mt-3 text-sm text-[#6b5b52]">Únete y accede a terapias, cursos y talleres para tu bienestar</p>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-5 rounded-3xl border border-[#e8ded4] bg-white p-8 shadow-[0_10px_40px_rgba(75,58,47,0.07)] sm:p-10">
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Nombre completo *</span>
              <input value={form.name} onChange={set("name")} placeholder="Ana Martínez" data-testid="register-name-input" className={inputCls} />
              <Err k="name" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">WhatsApp *</span>
              <input value={form.whatsapp} onChange={set("whatsapp")} placeholder="+52 55 1234 5678" data-testid="register-whatsapp-input" className={inputCls} />
              <Err k="whatsapp" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Correo electrónico *</span>
              <input type="email" value={form.email} onChange={set("email")} placeholder="tu@correo.com" data-testid="register-email-input" className={inputCls} />
              <Err k="email" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Confirma tu correo *</span>
              <input type="email" value={form.email_confirmation} onChange={set("email_confirmation")} placeholder="tu@correo.com" data-testid="register-email-confirm-input" className={inputCls} />
              <Err k="email_confirmation" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Ciudad *</span>
              <input value={form.city} onChange={set("city")} placeholder="Ciudad de México" data-testid="register-city-input" className={inputCls} />
              <Err k="city" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">País *</span>
              <select value={form.country} onChange={set("country")} data-testid="register-country-input" className={inputCls}>
                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Contraseña * (mín. 8)</span>
              <input type="password" value={form.password} onChange={set("password")} placeholder="••••••••" data-testid="register-password-input" className={inputCls} />
              <Err k="password" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#a47b52]">Confirma tu contraseña *</span>
              <input type="password" value={form.password_confirmation} onChange={set("password_confirmation")} placeholder="••••••••" data-testid="register-password-confirm-input" className={inputCls} />
              <Err k="password_confirmation" />
            </label>
          </div>

          {form.password && form.password_confirmation && form.password === form.password_confirmation && (
            <p className="flex items-center gap-2 text-xs text-[#3b7a57]"><Check size={14} /> Las contraseñas coinciden</p>
          )}
          {serverError && <p data-testid="register-server-error" className="rounded-xl bg-[#c85a48]/10 px-4 py-3 text-sm text-[#c85a48]">{serverError}</p>}

          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#4b3a2f] py-4 text-sm font-semibold text-[#f2e9dd] transition-colors hover:bg-[#c9a24b] hover:text-[#4b3a2f]"
          >
            {loading && <Loader2 size={16} className="animate-spin" />} Crear mi cuenta
          </button>
          <p className="text-center text-sm text-[#6b5b52]">
            ¿Ya tienes cuenta?{" "}
            <Link to="/iniciar-sesion" data-testid="register-to-login" className="font-semibold text-[#a47b52] hover:underline">
              Inicia sesión
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Register;
